/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { RequestOptions } from 'app/types/network';
import { MissingDrug } from 'app/types/drug';
import { List } from 'app/hooks/app-requests/crud/use-crud-list';

/**
 * Get missing drug occurrences.
 */

function getMissingDrugOccurrences(
  client: AxiosInstance,
  code: string,
  options?: RequestOptions
): Promise<List<MissingDrug>> {
  return client
    .get(
      resolveAppUrl('missingDrugsOccurrences', {
        interpolations: { code },
        params: options
      })
    )
    .then(({ data }) => {
      const results = data?.data?.results ?? data?.data ?? [];
      const total = data?.data?.total ?? results.length;

      return { results, total };
    });
}

/**
 * Export `useGetMissingDrugOccurrences`.
 */

export function useGetMissingDrugOccurrences(
  code: string | undefined,
  options?: RequestOptions & { pageSize?: number }
) {
  const client = useRequestProvider();

  return useQuery(['missingDrugOccurrences', code, options], {
    queryFn: async () => {
      if (!code) {
        return { results: [], total: 0 };
      }

      return await getMissingDrugOccurrences(client, code, options);
    }
  });
}
